// ./src/hooks/useOrganizerProfiles.ts
//
// Entity hook for the account's ORGANIZER PROFILES — the public pages a host puts
// their events on (ROADMAP A2b #54). Same state machine as useMarketingChannels
// (see em-scaffold-ui): scoped reads, optimistic collection helpers,
// concurrent-removal tracking, race guards, server pagination.
//
// ⚠ `total` comes from the RESULT, not from `items.length`. The picker on an event
// needs to know whether the account has exactly ONE profile (it auto-selects then),
// and "one loaded on this page" is not the same question as "one in total".
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import {
  OrganizerProfile,
  OrganizerProfileService,
  OrganizerProfileFilter,
  OrganizerProfileInput,
  Pagination,
  SortItem,
  listPage,
} from "@/utils/api";
import { upsertBy, upsertManyBy, removeBy, removeManyBy } from "@/utils";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

// ============================================
// Types
// ============================================

export interface UseOrganizerProfilesOptions {
  pageSize?: number;
  initialFilters?: Partial<OrganizerProfileFilter>;
  initialSort?: SortItem[];
  autoLoad?: boolean;
}

export interface UseOrganizerProfilesReturn {
  organizerProfile: OrganizerProfile | null;
  organizerProfiles: OrganizerProfile[];
  loading: boolean;
  isLoaded: boolean;
  /**
   * #198 (review) — the last LIST read failed (set by the read only — a refused create sets `lastError`, never this). ⚠ The
   * list is then EMPTY and `isLoaded` true, which reads as "no pages": a screen deciding "has a page?" asks this first.
   */
  loadFailed: boolean;
  isEmpty: boolean;

  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  isCreating: boolean;
  isUpdating: boolean;
  removingIds: Set<string>;
  isRemoving: (id?: string) => boolean;

  filters: Partial<OrganizerProfileFilter>;
  sortBy: SortItem[];

  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<OrganizerProfileFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  create: (params: OrganizerProfileInput) => Promise<OpResult>;
  update: (id: string, params: OrganizerProfileInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;

  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  reset: () => void;
  getById: (id: string | undefined) => OrganizerProfile | undefined;

  /** How many profiles the account has IN TOTAL (server-reported), not how many are loaded. */
  totalCount: number;

  lastError: OpResult | null;
  clearError: () => void;
}

// ============================================
// Defaults
// ============================================

const DEFAULT_SORT: SortItem[] = [{ name: "name", order: "ASC" }];
const DEFAULT_FILTERS: Partial<OrganizerProfileFilter> = {};

// ============================================
// Hook
// ============================================

export const useOrganizerProfiles = (
  options: UseOrganizerProfilesOptions = {}
): UseOrganizerProfilesReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = true,
  } = options;

  const { user } = useApp();
  const userId = user?.id ?? null;

  const pageSizeRef = useRef(pageSize);
  // The page size of the LAST read: a refresh re-runs that read, so a WHOLE_SET read stays whole (#118).
  const lastPageSizeRef = useRef<number>(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  const lastQueriedFilterRef = useRef<Partial<OrganizerProfileFilter>>(initialFilters);
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  const listCallIdRef = useRef(0);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  const [organizerProfile, setOrganizerProfile] = useState<OrganizerProfile | null>(null);
  const [organizerProfiles, setOrganizerProfiles] = useState<OrganizerProfile[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state — "No notes
   * yet", "No tasks yet" — before the skeleton and then the data. Reported across the whole event
   * space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState<Partial<OrganizerProfileFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  const [currentPage, setCurrentPage] = useState(1);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

  const [lastError, setLastError] = useState<OpResult | null>(null);

  const isEmpty = useMemo(
    () => isLoaded && organizerProfiles.length === 0,
    [isLoaded, organizerProfiles.length]
  );

  // ── Removal tracking ──
  const startRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));

      return next;
    });
  }, []);

  const stopRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));

      return next;
    });
  }, []);

  const isRemoving = useCallback(
    (id?: string): boolean => (id ? removingIds.has(id) : removingIds.size > 0),
    [removingIds]
  );

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    listCallIdRef.current++;

    setOrganizerProfile(null);
    setOrganizerProfiles([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadFailed(false);
    setLoadingMore(false);
    setHasMore(false);
    setTotalCount(0);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingIds(new Set());
  }, []);

  // ── Core reads ──
  const get = useCallback(async (id: string): Promise<OpResult> => {
    setLoading(true);
    setLastError(null);

    try {
      const result = await OrganizerProfileService.get(id);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = (result.data as OrganizerProfile[])[0];
        setOrganizerProfile(data ?? null);

        if (data) {
          setOrganizerProfiles((prev) => upsertBy(prev, data));
        }

        setLastError(null);

        return result;
      }

      setLastError(result);
      setOrganizerProfile(null);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);
      setOrganizerProfile(null);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  const list = useCallback(
    async (
      filterOverride: Partial<OrganizerProfileFilter> = {},
      pagination?: Pagination,
      sortByOverride?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      lastPageSizeRef.current = targetPageSize;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
        setCurrentPage(1);
      }

      setLastError(null);

      setFilters(filterOverride);
      lastQueriedFilterRef.current = filterOverride;

      if (sortByOverride) {
        setSortBy(sortByOverride);
        lastQueriedSortRef.current = sortByOverride;
      }

      const currentSortBy = sortByOverride || lastQueriedSortRef.current;

      try {
        const result = await listPage(
          OrganizerProfileService.list,
          filterOverride as OrganizerProfileFilter,
          { page: targetPage, pageSize: targetPageSize },
          currentSortBy
        );

        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data) ? (result.data as OrganizerProfile[]) : [];
          const loadedSoFar = isAppending
            ? (targetPage - 1) * targetPageSize + newItems.length
            : newItems.length;
          // ⚠ The server's total when it gives one; otherwise what is on screen. A
          // missing total must not read as "0 profiles" on a page that is showing some.
          const serverTotal = typeof result.total === "number" ? result.total : loadedSoFar;

          if (isAppending) {
            setOrganizerProfiles((prev) => upsertManyBy(prev, newItems, "id", "end"));
          } else {
            setOrganizerProfiles(newItems);
          }

          setTotalCount(serverTotal);
          setHasMore(loadedSoFar < serverTotal);
          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLoadFailed(false);
          setLastError(null);

          return result;
        }

        setLastError(result);
        setIsLoaded(true);
        setLoadFailed(true);

        if (!isAppending) {
          setOrganizerProfiles([]);
        }

        return result;
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(result);
        setIsLoaded(true);
        setLoadFailed(true);

        if (!isAppending) {
          setOrganizerProfiles([]);
        }

        return result;
      } finally {
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  // ── Mutations ──
  const create = useCallback(async (params: OrganizerProfileInput): Promise<OpResult> => {
    setIsCreating(true);
    setLastError(null);

    try {
      const result = await OrganizerProfileService.create(params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const newItem = (result.data as OrganizerProfile[])[0];
        setOrganizerProfile(newItem ?? null);

        if (newItem) {
          setOrganizerProfiles((prev) => upsertBy(prev, newItem));
          setTotalCount((prev) => prev + 1);
        }

        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsCreating(false);
    }
  }, []);

  const update = useCallback(
    async (id: string, params: OrganizerProfileInput): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Organizer page ID is required for updating"
        );
        setLastError(result);

        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await OrganizerProfileService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as OrganizerProfile[])[0];
          setOrganizerProfile(updatedItem ?? null);

          if (updatedItem) {
            setOrganizerProfiles((prev) => upsertBy(prev, updatedItem));
          }

          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Organizer page ID is required for removal"
        );
        setLastError(result);

        return result;
      }

      startRemoving([id]);
      setLastError(null);

      try {
        const result = await OrganizerProfileService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setOrganizerProfile(null);
          setOrganizerProfiles((prev) => removeBy(prev, id));
          setTotalCount((prev) => Math.max(0, prev - 1));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving([id]);
      }
    },
    [startRemoving, stopRemoving]
  );

  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "At least one organizer page must be selected"
        );
        setLastError(result);

        return result;
      }

      startRemoving(ids);
      setLastError(null);

      try {
        const result = await OrganizerProfileService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setOrganizerProfile(null);
          setOrganizerProfiles((prev) => removeManyBy(prev, ids));
          setTotalCount((prev) => Math.max(0, prev - ids.length));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving(ids);
      }
    },
    [startRemoving, stopRemoving]
  );

  // ── Convenience ──
  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    const nextPage = currentPage + 1;

    await list(
      lastQueriedFilterRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loading, loadingMore, hasMore, currentPage]);

  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFilterRef.current,
      { page: 1, pageSize: lastPageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(refresh, isLoaded);

  const getById = useCallback(
    (id: string | undefined): OrganizerProfile | undefined => {
      if (!id) {
        return undefined;
      }

      return organizerProfiles.find((item) => item.id === id);
    },
    [organizerProfiles]
  );

  // ── Effects ──
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list(initialFiltersRef.current, { page: 1, pageSize: pageSizeRef.current }, initialSortRef.current, true);
      }

      return;
    }

    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list(initialFiltersRef.current, { page: 1, pageSize: pageSizeRef.current }, initialSortRef.current, true);
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ── Return ──
  return useMemo(
    () => ({
      organizerProfile,
      organizerProfiles,
      loading,
      isLoaded,
      loadFailed,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      pageSize: pageSizeRef.current,
      isCreating,
      isUpdating,
      removingIds,
      isRemoving,
      filters,
      sortBy,
      get,
      list,
      create,
      update,
      remove,
      removeMany,
      loadMore,
      refresh,
      reset,
      getById,
      totalCount,
      lastError,
      clearError,
    }),
    [
      organizerProfile,
      organizerProfiles,
      loading,
      isLoaded,
      loadFailed,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      isCreating,
      isUpdating,
      removingIds,
      isRemoving,
      filters,
      sortBy,
      get,
      list,
      create,
      update,
      remove,
      removeMany,
      loadMore,
      refresh,
      reset,
      getById,
      totalCount,
      lastError,
      clearError,
    ]
  );
};
